import { z } from "zod";
import { type Question } from "./types";

// 参考: app/models/form/client_frontend/client_hold_enquete.rb の SIBLING_OR_REQUIRED_QUESTION_CODES
export const SIBLING_OR_REQUIRED_QUESTION_CODES = [
  "q3_specific_new_shop_period",
  "q3_specific_new_shop_area",
] as const;

// period(オープン時期を選択する) / area(エリアを入力する) の OR 必須グループは、
// q3_specific_reason_new_shop(新店舗のオープン、事業拡大のため)の選択肢が選ばれたときだけ表示する
export const NEW_SHOP_GROUP_TRIGGER = {
  questionCode: "q3_specific_reason",
  optionCode: "q3_specific_reason_new_shop",
} as const;

const MAX_TEXT_LENGTH = 1000;

const MESSAGES = {
  requiredChoice: "必須項目です。少なくとも1つ選択してください。",
  requiredFollowUp: "その他の詳細をご記入ください",
  tooLong: `${MAX_TEXT_LENGTH}文字以内で入力してください。`,
  newShopOrRequired: "必須項目です。少なくともどちらかご入力（選択）してください。",
} as const;

// 各設問の回答をフラットに持つオブジェクト。key は questionCode（follow_up は followUpKey で生成）。
export type HoldEnqueteFormValues = Record<string, string | string[] | undefined>;

export const followUpKey = (questionCode: string): string => `${questionCode}__follow_up`;

// q3_specific_new_shop_period は "YYYY-MM" 文字列を送るが、UI では年・月を独立セレクトで持つため
// __year / __month のサブフィールドで保持し、送信時に合成する（utils.ts）。
export const yearKey = (questionCode: string): string => `${questionCode}__year`;
export const monthKey = (questionCode: string): string => `${questionCode}__month`;

const isNewShopPeriod = (q: Question): boolean =>
  q.questionCode === SIBLING_OR_REQUIRED_QUESTION_CODES[0];

const isPeriodFilled = (values: HoldEnqueteFormValues, periodCode: string): boolean => {
  const year = values[yearKey(periodCode)];
  const month = values[monthKey(periodCode)];
  return typeof year === "string" && year !== "" && typeof month === "string" && month !== "";
};

// 親選択肢が選ばれている子設問のみ表示する
const isVisible = (q: Question, values: HoldEnqueteFormValues): boolean => {
  if (q.parentQuestionCode === null || q.parentOptionCode === null) return true;
  const parentSelected = values[q.parentQuestionCode];
  return Array.isArray(parentSelected) && parentSelected.includes(q.parentOptionCode);
};

// 「その他」の選択肢が選ばれている場合にテキスト入力を必須とする
const isFollowUpRequired = (q: Question, values: HoldEnqueteFormValues): boolean => {
  if (q.questionType === "free_text") return false;
  const selected = values[q.questionCode];
  if (!Array.isArray(selected)) return false;
  return q.options.some((opt) => opt.requiresFollowUpText && selected.includes(opt.optionCode));
};

const isStringEmpty = (value: unknown): boolean =>
  typeof value !== "string" || value.trim().length === 0;

// 渡された questions に合わせて、入力欄の型と必須チェックなどの
// バリデーションをまとめた Zod スキーマを組み立てる。
// 休止アンケートは再開予定日によって表示する設問が変わるため、
// 固定スキーマで書かずにその場で動的に生成する。
export const buildSchema = (questions: Question[]) => {
  const shape: Record<string, z.ZodTypeAny> = {};

  questions.forEach((q) => {
    if (isNewShopPeriod(q)) {
      shape[yearKey(q.questionCode)] = z.string().default("");
      shape[monthKey(q.questionCode)] = z.string().default("");
      return;
    }
    if (q.questionType === "free_text") {
      shape[q.questionCode] = z.string().default("");
    } else {
      shape[q.questionCode] = z.array(z.string()).default([]);
      if (q.options.some((opt) => opt.requiresFollowUpText)) {
        shape[followUpKey(q.questionCode)] = z.string().default("");
      }
    }
  });

  return z.object(shape).check((ctx) => {
    const values = ctx.value as HoldEnqueteFormValues;

    questions.forEach((q) => {
      // 親未選択の子設問を検証対象外にする
      if (!isVisible(q, values)) return;

      const isSiblingOrRequired = (
        SIBLING_OR_REQUIRED_QUESTION_CODES as readonly string[]
      ).includes(q.questionCode);

      // 少なくとも1つ選択する必須設問のチェック
      if (q.required && !isSiblingOrRequired && q.questionType !== "free_text") {
        const selected = values[q.questionCode];
        if (!Array.isArray(selected) || selected.length === 0) {
          ctx.issues.push({
            code: "custom",
            path: [q.questionCode],
            message: MESSAGES.requiredChoice,
            input: values,
          });
        }
      }

      // 自由記述（free_text）の文字数超過チェック
      if (q.questionType === "free_text") {
        const value = values[q.questionCode];
        if (typeof value === "string" && value.length > MAX_TEXT_LENGTH) {
          ctx.issues.push({
            code: "custom",
            path: [q.questionCode],
            message: MESSAGES.tooLong,
            input: values,
          });
        }
      }

      // 「その他」選択時の追加テキスト入力の必須・文字数チェック
      if (isFollowUpRequired(q, values)) {
        const followUp = values[followUpKey(q.questionCode)];
        if (isStringEmpty(followUp)) {
          ctx.issues.push({
            code: "custom",
            path: [followUpKey(q.questionCode)],
            message: MESSAGES.requiredFollowUp,
            input: values,
          });
        } else if (typeof followUp === "string" && followUp.length > MAX_TEXT_LENGTH) {
          ctx.issues.push({
            code: "custom",
            path: [followUpKey(q.questionCode)],
            message: MESSAGES.tooLong,
            input: values,
          });
        }
      }
    });

    // 「新店舗のオープン、事業拡大のため」配下の period / area の OR 必須チェック（両方空のときだけエラー）
    // 両フィールドを赤枠にするため、エラーは両方の path に push する
    const parentSelected = values[NEW_SHOP_GROUP_TRIGGER.questionCode];
    const isNewShopSelected =
      Array.isArray(parentSelected) && parentSelected.includes(NEW_SHOP_GROUP_TRIGGER.optionCode);

    if (isNewShopSelected) {
      const [periodCode, areaCode] = SIBLING_OR_REQUIRED_QUESTION_CODES;
      // period は年月セレクト（両方選ばれて初めて「入力あり」扱い）
      const periodFilled = isPeriodFilled(values, periodCode);
      const areaFilled = !isStringEmpty(values[areaCode]);
      if (!periodFilled && !areaFilled) {
        SIBLING_OR_REQUIRED_QUESTION_CODES.forEach((code) => {
          ctx.issues.push({
            code: "custom",
            path: [code],
            message: MESSAGES.newShopOrRequired,
            input: values,
          });
        });
      }
    }
  });
};
